import { ApplicationRef, Component, DOCUMENT, inject, Inject, OnInit, AfterViewInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import {
    UnxMetaPixelService,
    UnxScriptInjectorService,
    RealtimeNotificationService,
    UnxRouteLoadingService,
    PublicSseService,
    BrandScriptRegistryService,
    ScriptLoaderService,
    ChatCleanupResolver,
    UnxGlobalDialogService,
} from '@unx/wdk/shared/services';
import { LbwBrandSettingsModel } from '@unx/wdk/shared/model';
import { BRAND_SETTINGS_TOKEN, TOASTER_COMPONENT } from '@unx/wdk/shared/tokens';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
    title = 'lobu-publish';
    private timer: ReturnType<typeof setTimeout> | undefined;
    private _splashMaxTimer: ReturnType<typeof setTimeout> | undefined;
    private _splashRemoved = false;
    private destroy$: Subject<void> = new Subject<void>();
    /** Tracked from NavigationStart so NavigationEnd knows whether to scroll-to-top. */
    private _lastNavTrigger: 'imperative' | 'popstate' | 'hashchange' = 'imperative';

    private _appRef = inject(ApplicationRef);
    private _scriptLoaderService = inject(ScriptLoaderService);
    private _brandScriptRegistry = inject(BrandScriptRegistryService);
    private _cleanupResolver = inject(ChatCleanupResolver);
    private _publicSseService = inject(PublicSseService);
    private _globalDialogService = inject(UnxGlobalDialogService);
    private _toasterComponent = inject(TOASTER_COMPONENT, { optional: true });

    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _scriptInjectorService: UnxScriptInjectorService,
        private router: Router,
        private _routeLoadingService: UnxRouteLoadingService,
        private _pixelService: UnxMetaPixelService,
        private _realtimeNotificationService: RealtimeNotificationService,
        @Inject(BRAND_SETTINGS_TOKEN) public _brandSettings: LbwBrandSettingsModel,
    ) {
        this._routeLoadingService.init();

        /*
         * iOS Chrome scroll-restore opt-out:
         * Tarayıcının kendi pageshow/BFCache scroll restoration'ı bizim
         * `window.scrollTo(0,0)` çağrımızı geç override ediyor. `'manual'` ile
         * tarayıcıya "scroll'u sen yönetme" diyoruz; popstate restoration'ı
         * Angular Router (scrollPositionRestoration:'enabled') yapacak.
         */
        if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        this.router.events
            .pipe(
                filter((event: any) => event instanceof NavigationEnd),
                take(1),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this._scheduleSplashHide();
            });

        /*
         * Trigger-aware scroll-to-top:
         * iOS Chrome'da Angular'ın yerleşik `scrollPositionRestoration: 'enabled'` mekanizması
         * imperative navigasyonlarda güvenilmez (önceki sayfanın scroll pozisyonu sızıyor).
         * `imperative` (link tıklaması / programatik) ise üste scroll'lıyoruz; `popstate`
         * (tarayıcı geri/ileri) ise dokunmuyoruz, tarayıcı native restore yapsın.
         * 3 atış: anında, rAF, setTimeout — iOS Chrome'un geç tetiklenen restore'unu kapsa.
         */
        this.router.events
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                if (event instanceof NavigationStart) {
                    this._lastNavTrigger = event.navigationTrigger ?? 'imperative';
                } else if (event instanceof NavigationEnd && this._lastNavTrigger === 'imperative') {
                    this._scrollToTop();
                    this._startScrollWatchdog();
                }
            });
    }

    private _scrollWatchdogRafId: number | null = null;
    private _scrollWatchdogStart = 0;

    /**
     * iOS Chrome scroll-restore watchdog: navigasyondan sonra 1sn boyunca her
     * frame'de scroll'u kontrol eder; 0'dan farklı ise tekrar 0'a çeker.
     * iOS Chrome'un BFCache/pageshow tetikli late restoration'ını bastırır.
     */
    private _startScrollWatchdog(): void {
        const WATCHDOG_MS = 1000;
        if (this._scrollWatchdogRafId !== null) {
            cancelAnimationFrame(this._scrollWatchdogRafId);
        }
        this._scrollWatchdogStart = performance.now();
        const tick = (): void => {
            const elapsed = performance.now() - this._scrollWatchdogStart;
            if (elapsed > WATCHDOG_MS) {
                this._scrollWatchdogRafId = null;
                return;
            }
            this._scrollToTop();
            this._scrollWatchdogRafId = requestAnimationFrame(tick);
        };
        this._scrollWatchdogRafId = requestAnimationFrame(tick);
    }

    /**
     * Exhaustive scroll-to-top:
     * 1) window.scrollTo — modern API (window scroll'u varsa)
     * 2) document.scrollingElement — canonical scroll container
     * 3) documentElement + body — legacy WebKit/Chrome ayrımı
     * 4) `.layout-cx2f-container` — bu tema'da ASIL scroll container'ı (direkt hedef)
     * 5) Tüm overflow-auto/scroll element'leri — fallback
     */
    private _scrollToTop(): void {
        try {
            window.scrollTo(0, 0);
            const scrollingEl = this._document.scrollingElement as HTMLElement | null;
            if (scrollingEl) {
                scrollingEl.scrollTop = 0;
                scrollingEl.scrollLeft = 0;
            }
            if (this._document.documentElement) {
                this._document.documentElement.scrollTop = 0;
            }
            if (this._document.body) {
                this._document.body.scrollTop = 0;
            }
            /* Asıl scroll container — CX2F tema layout wrapper'ı */
            const cx2fContainers = this._document.querySelectorAll<HTMLElement>('.layout-cx2f-container');
            cx2fContainers.forEach(el => {
                el.scrollTop = 0;
                el.scrollLeft = 0;
            });
            /* Fallback: app-root içindeki tüm scrollable container'lar */
            const root = this._document.querySelector('app-root');
            if (root) {
                const scrollables = root.querySelectorAll<HTMLElement>('*');
                for (let i = 0; i < scrollables.length; i++) {
                    const el = scrollables[i];
                    if (el.scrollTop !== 0) {
                        const overflowY = getComputedStyle(el).overflowY;
                        if (overflowY === 'auto' || overflowY === 'scroll') {
                            el.scrollTop = 0;
                        }
                    }
                }
            }
        } catch {
            /* never block navigation on scroll errors */
        }
    }

    ngOnInit(): void {
        this._scriptInjectorService.addScript();
        this._pixelService.initialize();
        if (this._toasterComponent) {
            this._realtimeNotificationService.init(this._toasterComponent);
        }

        this.router.events
            .pipe(
                filter((ev: any) => ev instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.handleRouteChange());
    }

    ngAfterViewInit(): void {
        this._publicSseService.init();

        this._globalDialogService.initDefaultPromotionDialog();
    }

    private handleRouteChange() {
        const url = this.router.url;

        const scripts = this._brandScriptRegistry.getScriptsForBrand(
            this._brandSettings,
            url,
        );

        const cleanupStrategy = this._cleanupResolver.getStrategy(
            this._brandSettings.chatProvider || '',
        );

        const anyShouldLoad = scripts.some((s: { loadCondition: () => any }) =>
            s.loadCondition(),
        );

        if (!anyShouldLoad) {
            cleanupStrategy?.cleanup();
            this._scriptLoaderService.unloadAll();
            return;
        }

        scripts.forEach((script: { id: string; providerScriptUrl: any }) => {
            const alreadyExists = document.getElementById(script.id);
            if (alreadyExists) return;

            this._scriptLoaderService.loadInlineScript(
                script.id,
                script.providerScriptUrl,
            );
        });
    }

    /**
     * `data-splash-strategy="content-ready"` on #splash-screen: hide after load + stable + initial images, capped.
     * Otherwise: fixed delay (legacy).
     */
    private _scheduleSplashHide(): void {
        const splash = this._document.getElementById('splash-screen');
        if (splash?.dataset['splashStrategy'] === 'content-ready') {
            void this._hideSplashWhenContentReady();
            return;
        }
        this._hideSplashLegacy();
    }

    private _hideSplashLegacy(): void {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        this.timer = setTimeout(() => this._removeSplashOnce(), 1500);
    }

    private _removeSplashOnce(): void {
        if (this._splashRemoved) {
            return;
        }
        this._splashRemoved = true;
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = undefined;
        }
        if (this._splashMaxTimer) {
            clearTimeout(this._splashMaxTimer);
            this._splashMaxTimer = undefined;
        }
        this._document.body.classList.remove('overflow-hidden');
        const el = this._document.getElementById('splash-screen') as HTMLElement | null;
        el?.classList.add('page-loaded');
    }

    private async _hideSplashWhenContentReady(): Promise<void> {
        const MIN_MS = 480;
        const MAX_MS = 4400;
        const IMG_PHASE_MS = 2600;
        const WINDOW_LOAD_CAP_MS = 3200;
        const started = performance.now();

        this._splashMaxTimer = setTimeout(() => this._removeSplashOnce(), MAX_MS);

        try {
            await Promise.race([
                this._whenWindowLoaded(),
                new Promise<void>((r) => setTimeout(r, WINDOW_LOAD_CAP_MS)),
            ]);
            await this._whenStableOnce();
            await this._afterNextPaint();
            const elapsed = performance.now() - started;
            const imgBudget = Math.max(
                200,
                Math.min(IMG_PHASE_MS, MAX_MS - elapsed - 350),
            );
            await this._settleInitialAppImages(imgBudget);
            const elapsed2 = performance.now() - started;
            const minWait = Math.max(0, MIN_MS - elapsed2);
            if (minWait > 0) {
                await new Promise<void>((r) => setTimeout(r, minWait));
            }
        } catch {
            /* splash must not block the app */
        } finally {
            if (this._splashMaxTimer) {
                clearTimeout(this._splashMaxTimer);
                this._splashMaxTimer = undefined;
            }
            this._removeSplashOnce();
        }
    }

    private _whenWindowLoaded(): Promise<void> {
        const w = this._document.defaultView;
        if (!w || this._document.readyState === 'complete') {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            w.addEventListener('load', () => resolve(), { once: true });
        });
    }

    private async _whenStableOnce(): Promise<void> {
        const STABLE_WAIT_MS = 2500;
        await Promise.race([
            firstValueFrom(this._appRef.isStable.pipe(filter(Boolean), take(1))),
            new Promise<void>((r) => setTimeout(r, STABLE_WAIT_MS)),
        ]);
    }

    private _afterNextPaint(): Promise<void> {
        return new Promise((resolve) => {
            const w = this._document.defaultView;
            if (!w) {
                resolve();
                return;
            }
            w.requestAnimationFrame(() => {
                w.requestAnimationFrame(() => resolve());
            });
        });
    }

    private async _settleInitialAppImages(budgetMs: number): Promise<void> {
        const root = this._document.querySelector('app-root');
        if (!root || budgetMs <= 0) {
            return;
        }
        const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img[src]')).slice(
            0,
            24,
        );
        if (!imgs.length) {
            return;
        }
        const waitOne = (img: HTMLImageElement): Promise<void> => {
            if (img.complete && img.naturalWidth > 0) {
                return Promise.resolve();
            }
            return new Promise((resolve) => {
                const done = (): void => resolve();
                img.addEventListener('load', done, { once: true });
                img.addEventListener('error', done, { once: true });
            });
        };
        await Promise.race([
            Promise.all(imgs.map(waitOne)),
            new Promise<void>((r) => setTimeout(r, budgetMs)),
        ]);
    }

    ngOnDestroy() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        if (this._splashMaxTimer) {
            clearTimeout(this._splashMaxTimer);
        }
        if (this._scrollWatchdogRafId !== null) {
            cancelAnimationFrame(this._scrollWatchdogRafId);
            this._scrollWatchdogRafId = null;
        }

        this.destroy$.next();
        this.destroy$.complete();
        this._scriptLoaderService.unloadAll();
    }
}
